import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, Check, X } from "lucide-react";
import {
  type Channel,
  getNotifications,
  type NotificationPrefs,
  setNotifications,
} from "../api";

const CHANNELS: { value: Channel; label: string }[] = [
  { value: "email", label: "Email" },
  { value: "sms", label: "Text" },
  { value: "both", label: "Both" },
];

/** A 3-way Email / Text / Both selector for one category. Text/Both are disabled
 * until the user has opted into SMS (consent + phone). */
function ChannelPicker({
  value,
  onChange,
  smsReady,
}: {
  value: Channel;
  onChange: (c: Channel) => void;
  smsReady: boolean;
}) {
  return (
    <div className="inline-flex rounded-md border border-ink/20 overflow-hidden">
      {CHANNELS.map((c) => {
        const needsSms = c.value !== "email" && !smsReady;
        const active = value === c.value;
        return (
          <button
            key={c.value}
            type="button"
            disabled={needsSms}
            onClick={() => onChange(c.value)}
            className={
              "px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider cs-tap border-r border-ink/20 last:border-r-0 " +
              (active
                ? "bg-accent text-ink"
                : needsSms
                  ? "bg-bg text-ink/30 cursor-not-allowed"
                  : "bg-bg text-ink/60 hover:bg-bg-2")
            }
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

export function NotificationsScreen({
  onClose,
  onManageTexts,
}: {
  onClose: () => void;
  onManageTexts: () => void;
}) {
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [sessions, setSessions] = useState<Channel>("email");
  const [beacons, setBeacons] = useState<Channel>("email");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getNotifications()
      .then((p) => {
        if (cancelled) return;
        setPrefs(p);
        setSessions(p.channel_sessions);
        setBeacons(p.channel_beacons);
      })
      .catch(() => undefined);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      cancelled = true;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const smsReady = !!(prefs?.consent && prefs?.phone_e164);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const p = await setNotifications({
        channel_sessions: sessions,
        channel_beacons: beacons,
      });
      setPrefs(p);
      setSaved(true);
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 bg-bg overflow-y-auto cs-modal-overlay">
      <header className="sticky top-0 z-10 bg-bg/95 backdrop-blur border-b border-ink/10">
        <div className="max-w-lg mx-auto px-5 py-3 flex items-center justify-between">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55">
            Notifications
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 -mr-1 flex items-center justify-center text-ink/55 hover:text-ink cs-tap"
          >
            <X size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-5 py-8 pb-20">
        <div className="w-12 h-12 rounded-lg bg-ink flex items-center justify-center mb-4">
          <Bell size={22} className="text-accent" />
        </div>
        <h1 className="font-display font-bold text-3xl tracking-tight mb-2">
          How should we reach you?
        </h1>
        <p className="text-ink/65 leading-relaxed mb-6">
          Pick email, text, or both — separately for your weekly schedule and for
          last-minute pickup games.
        </p>

        {!smsReady && (
          <div className="mb-6 border border-ink/15 bg-bg-2 rounded-md px-4 py-3">
            <p className="text-sm text-ink/70 mb-2">
              Turn on texts to choose <strong>Text</strong> or <strong>Both</strong>.
            </p>
            <button
              onClick={onManageTexts}
              className="font-mono text-[11px] uppercase tracking-wider text-ink underline underline-offset-2 hover:text-ink/70 cs-tap"
            >
              Set up text notifications →
            </button>
          </div>
        )}

        <div className="space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-display font-bold text-base">Weekly sessions</div>
              <div className="text-xs text-ink/55">Schedule digest & game reminders</div>
            </div>
            <ChannelPicker value={sessions} onChange={(c) => { setSessions(c); setSaved(false); }} smsReady={smsReady} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-display font-bold text-base">Pickup beacons</div>
              <div className="text-xs text-ink/55">Last-minute "anyone to play?" invites</div>
            </div>
            <ChannelPicker value={beacons} onChange={(c) => { setBeacons(c); setSaved(false); }} smsReady={smsReady} />
          </div>
        </div>

        {error && <div className="text-sm text-[#C84B31] mt-5">{error}</div>}

        {saved && (
          <div className="mt-5 flex items-center gap-2 border border-success/40 bg-success/10 rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-success">
            <Check size={14} /> Saved
          </div>
        )}

        <button
          onClick={() => void save()}
          disabled={busy}
          className="mt-7 w-full px-5 py-3 bg-accent text-ink font-display font-bold text-base rounded-lg cs-tap disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105"
        >
          {busy ? "Saving…" : "Save preferences"}
        </button>

        <p className="mt-5 text-xs text-ink/50 leading-relaxed">
          You can also text STOP anytime to stop all texts.
        </p>
      </main>
    </div>,
    document.body,
  );
}
